import React from 'react';
import { motion } from 'framer-motion';
import VideoPreview from '../VideoPreview/VideoPreview';
import { useCardCompany } from '../../hooks/useCardCompany';
import { useCardCompanyUI } from '../../hooks/useCardCompanyUI';
import { useCardCompanyScroll } from '../../hooks/useCardCompanyScroll';
import styles from './CardCompany.module.css';

const CardCompany = ({ 
    companyId = "", 
    size = "Desktop 1440",
    onClose = () => {} 
}) => {
    // Obtener datos y estados
    const {
        companyData,
        footerData,
        formattedAddedTime,
        isLoading,
        error
    } = useCardCompany(companyId);

    // Obtener funcionalidad UI y referencias
    const {
        contentRef,
        swipeAreaRef,
        swipeDistance,
        isSwipeActive,
        isGrabHandleVisible,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleLinkClick,
        getVideoPreviewSize
    } = useCardCompanyUI({ onClose });

    // Obtener utilidades de scroll
    const { getSizeClass } = useCardCompanyScroll();

    if (isLoading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error || !companyData || !footerData) {
        return null;
    }

    const { 
        company_overview: {
            companyname,
            description,
            companylink,
            button_color
        },
        categories: { name: categoryName }
    } = companyData;

    const sizeClass = getSizeClass(size);

    return (
        <motion.div
            className={styles.cardCompany}
            style={{ transform: `translateY(${swipeDistance}px)` }}
            transition={swipeDistance === 0 ? { duration: 0.3, ease: "easeOut" } : {}}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div 
                className={`${styles.swipeArea} ${isGrabHandleVisible ? styles.visible : ''}`}
                ref={swipeAreaRef}
            >
                <div className={styles.grabHandle} />
            </div>

            <div 
                className={`${styles.contentWrapper} ${isSwipeActive ? styles.noScroll : ''}`}
                ref={contentRef}
            >
                <div className={`${styles.content} ${styles[sizeClass]}`}>
                    <h2 className={styles.companyName}>{companyname}</h2>
                    
                    <div 
                        className={styles.videoContainer}
                        onClick={() => handleLinkClick(companylink)}
                    >
                        <VideoPreview
                            companyId={companyData.companyoverviewid}
                            size={getVideoPreviewSize(size)}
                        />
                    </div>

                    <div className={styles.companyInfo}>
                        <div className={styles.header}>
                            <div className={styles.category}>
                                <span>{categoryName}</span>
                            </div>
                            
                            <button 
                                className={styles.visitSite}
                                style={{ backgroundColor: button_color }}
                                onClick={() => handleLinkClick(companylink)}
                            >
                                <span>{size === "Phone" ? "Visit" : "Visit site"}</span>
                            </button>

                            <div className={styles.addedTime}>
                                {formattedAddedTime}
                            </div>
                        </div>

                        <div className={styles.description}>
                            {description}
                        </div>

                        <div className={styles.keyFeatures}>
                            {[1, 2, 3, 4, 5].map((num) => {
                                const title = companyData.company_overview[`keyfeature${num}_title`];
                                const desc = companyData.company_overview[`keyfeature${num}_description`];
                                
                                if (!title) return null;
                                
                                return (
                                    <div key={`feature-${num}`} className={styles.keyFeature}>
                                        <h3 className={styles.featureTitle}>{title}</h3>
                                        <p className={styles.featureDescription}>{desc}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <footer className={styles.footer}>
                <div className={`${styles.footerContent} ${styles[sizeClass]}`}>
                    <div className={styles.logoContainer}>
                        <img 
                            src={footerData.logoimage} 
                            alt="Company Logo" 
                            className={styles.logo}
                        />
                    </div>

                    <div className={styles.footerText}>
                        <span className={styles.regularText}>{footerData.footertext1}</span>
                        <span className={styles.boldText}>{footerData.footertext2}</span>
                    </div>

                    <button className={styles.closeButton} onClick={onClose}>
                        <div className={styles.closeBackground}>
                            <img 
                                src={footerData.closeicon} 
                                alt="Close" 
                                className={styles.closeIcon}
                            />
                        </div>
                    </button>
                </div>
            </footer>
        </motion.div>
    );
};

export default CardCompany;